import { useState } from 'react';
import { faqs, reportSteps, trustPrinciples } from '@/mocks/blackinfo';

export default function TrustFaq() {
  const [open, setOpen] = useState<number>(0);
  const [tip, setTip] = useState('');

  const submitTip = (e: React.FormEvent) => {
    e.preventDefault();
    // 纯静态展示，不接接口
    setTip('已收到线索（演示环境不产生真实提交）——整理素材后可再来补充细节。');
    (e.currentTarget as HTMLFormElement).reset();
  };

  return (
    <section id="trust" className="mx-auto max-w-[1280px] px-4 pt-12 md:px-6 md:pt-16">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* 投稿爆料 + 流程 */}
        <div id="report" className="flex scroll-mt-24 flex-col rounded-lg border border-stone-200 bg-white p-5 md:p-7">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-600">
            <i className="ri-flashlight-fill" /> 投稿入口
          </span>
          <h2 className="mt-3 font-display text-xl font-bold text-stone-950 md:text-2xl">
            手里有新料？给我们递个线索
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-stone-500">
            素材越完整越利于核实。提交后默认匿名，可自由补充联系方式用于跟进。
          </p>

          <form onSubmit={submitTip} className="mt-5 space-y-3">
            <div>
              <label className="text-xs font-medium text-stone-600">一句话描述这条料（建议含时间与主体）</label>
              <textarea
                name="tip_title"
                maxLength={500}
                placeholder="例：某头部直播团队在 X 月 X 日的结算口径与到账金额不一致……"
                className="mt-1.5 w-full resize-none rounded-md border border-stone-200 bg-stone-50 px-3.5 py-3 text-sm text-stone-800 outline-none transition-colors focus:border-stone-400 focus:bg-white"
                rows={3}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">可附链接或截图说明（非必填）</label>
              <input
                type="text"
                name="tip_source"
                placeholder="粘贴原始出处链接，如微博/公号/工商查询页"
                className="mt-1.5 w-full rounded-md border border-stone-200 bg-stone-50 px-3.5 py-2.5 text-sm text-stone-800 outline-none transition-colors focus:border-stone-400 focus:bg-white"
              />
            </div>
            {/* 反垃圾蜜罐字段（对用户隐藏） */}
            <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="form-honeypot" />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white whitespace-nowrap transition-colors hover:bg-red-700"
              >
                <i className="ri-send-plane-line" /> 匿名提交线索
              </button>
              <span className="inline-flex items-center gap-1.5 text-xs text-stone-400">
                <i className="ri-shield-check-line" /> 您的信息仅用于内部核实
              </span>
            </div>
            {tip && (
              <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">
                {tip}
              </p>
            )}
          </form>

          {/* 投稿流程 */}
          <div className="mt-7 border-t border-stone-100 pt-6">
            <p className="text-xs font-semibold tracking-wide text-stone-400">一份线索的处理路径</p>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {reportSteps.map((r) => (
                <div key={r.step} className="flex flex-col">
                  <span className="font-display text-lg font-black text-stone-200">{r.step}</span>
                  <h3 className="mt-1 text-sm font-semibold text-stone-800">{r.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-stone-500">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 信任 / FAQ */}
        <div className="flex flex-col gap-5">
          <div className="rounded-lg border border-stone-200 bg-white p-5 md:p-7">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-stone-950 md:text-2xl">
              <i className="ri-shield-check-fill text-amber-500" /> 我们怎么核实
            </h2>
            <p className="mt-1 text-sm text-stone-500">快讯可以快，但每条结论都要对得起标出来的状态。</p>
            <ul className="mt-4 space-y-2.5">
              {trustPrinciples.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-stone-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-red-500" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex-1 rounded-lg border border-stone-200 bg-white p-5 md:p-7">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-stone-950 md:text-xl">
              <i className="ri-question-answer-line text-amber-500" /> 常见疑问
            </h2>
            <div className="mt-4 divide-y divide-stone-100">
              {faqs.map((f, idx) => {
                const isOpen = open === idx;
                return (
                  <div key={idx} className="py-3">
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : idx)}
                      className="flex w-full items-center justify-between gap-3 text-left"
                    >
                      <span className={'text-sm font-semibold ' + (isOpen ? 'text-red-700' : 'text-stone-800')}>
                        {f.q}
                      </span>
                      <i
                        className={
                          'ri-arrow-down-s-line text-lg text-stone-400 transition-transform ' +
                          (isOpen ? 'rotate-180 text-red-500' : '')
                        }
                      />
                    </button>
                    <div className={'grid transition-all duration-300 ' + (isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}>
                      <p className="overflow-hidden pt-2 text-sm leading-relaxed text-stone-500">{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}